import type { FieldError, FieldValues, Resolver } from 'react-hook-form';

/**
 * Wraps a resolver so an empty required select reads "Select a carrier" instead of the
 * schema's technical message for a missing id or enum value.
 */
export function withRequiredMessages<T extends FieldValues>(
  resolver: Resolver<T>,
  messages: Partial<Record<keyof T & string, string>>,
): Resolver<T> {
  return async (values, context, options) => {
    const result = await resolver(values, context, options);
    const errors = result.errors as Record<string, FieldError | undefined>;
    const raw = values as Record<string, unknown>;
    for (const [field, message] of Object.entries(messages)) {
      const value = raw[field];
      const empty = value === '' || value === null || value === undefined;
      if (errors[field] && empty) errors[field] = { ...errors[field], message: message as string };
    }
    return result;
  };
}

/** Adds one field error to a resolver result (used for rules the shared schema cannot know). */
export function addFieldError<T extends FieldValues>(
  result: Awaited<ReturnType<Resolver<T>>>,
  field: keyof T & string,
  message: string,
): Awaited<ReturnType<Resolver<T>>> {
  return {
    values: {},
    errors: { ...result.errors, [field]: { type: 'validate', message } },
  } as Awaited<ReturnType<Resolver<T>>>;
}
